/* eslint-disable @typescript-eslint/no-deprecated, eqeqeq, @typescript-eslint/prefer-nullish-coalescing */
import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { TableToolbar } from './table-toolbar';

export interface Column<T> {
  key: keyof T | string;
  header: string;
  render?: (value: T[keyof T], record: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  className?: string;
  /** Скрыть колонку на мобильных устройствах (< md) */
  hiddenOnMobile?: boolean;
}

export interface SortState {
  field: string;
  order: 'asc' | 'desc';
}

export interface BulkAction<T> {
  label: string;
  icon?: React.ReactNode;
  onClick: (selectedRows: T[]) => void;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost';
  /** Показывать только когда выбраны строки */
  showOnlyWhenSelected?: boolean;
}

interface DataTableProps<T extends { id: number | string }> {
  columns: Column<T>[];
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  isLoading?: boolean;
  onPageChange: (page: number) => void;
  onSearch?: (search: string) => void;
  searchValue?: string;
  filters?: React.ReactNode;
  filtersCount?: number;
  filterLabel?: string;
  filterTitle?: string;
  onResetFilters?: () => void;
  onRowClick?: (record: T) => void;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /** @deprecated Используйте bulkActions вместо actions */
  actions?: (record: T) => React.ReactNode;
  /** Bulk actions - действия над выбранными записями */
  bulkActions?: BulkAction<T>[];
  /** Текущая сортировка (для серверной сортировки) */
  sort?: SortState;
  /** Callback при изменении сортировки */
  onSortChange?: (sort: SortState | undefined) => void;
  /** Включить выбор строк */
  selectable?: boolean;
  /** ID строки для ключа (по умолчанию "id") */
  rowKey?: keyof T;
  /** Callback при изменении выбранных строк */
  onSelectionChange?: (rows: T[]) => void;
}

/**
 * Reusable data table component with pagination, search, sorting, and bulk actions
 */
export function DataTable<T extends { id: number | string }>({
  columns,
  data,
  total,
  page,
  pageSize,
  isLoading = false,
  onPageChange,
  onSearch,
  searchValue,
  filters,
  filtersCount,
  filterLabel,
  filterTitle,
  onResetFilters,
  onRowClick,
  searchPlaceholder = 'Поиск...',
  emptyMessage = 'Нет данных',
  actions,
  bulkActions,
  sort,
  onSortChange,
  selectable = false,
  rowKey = 'id' as keyof T,
  onSelectionChange,
}: DataTableProps<T>) {
  const [internalSearchValue, setInternalSearchValue] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

  const currentSearchValue = searchValue ?? internalSearchValue;

  const totalPages = Math.ceil(total / pageSize);
  const canPreviousPage = page > 1;
  const canNextPage = page < totalPages;

  // Определяем, используем ли bulk actions или старые actions
  const useBulkActions = bulkActions && bulkActions.length > 0;
  const showCheckboxes = selectable || useBulkActions;

  const handleSearch = (value: string) => {
    if (searchValue === undefined) {
      setInternalSearchValue(value);
    }
    onSearch?.(value);
  };

  const getValue = (record: T, key: string): T[keyof T] => {
    const keys = key.split('.');
    let value: unknown = record;
    for (const k of keys) {
      if (value != null && typeof value === 'object') {
        value = (value as Record<string, unknown>)[k];
      } else {
        return undefined as T[keyof T];
      }
    }
    return value as T[keyof T];
  };

  // Сортировка
  const handleSort = useCallback(
    (field: string) => {
      if (!onSortChange) {
        return;
      }

      if (sort?.field === field) {
        if (sort.order === 'asc') {
          onSortChange({ field, order: 'desc' });
        } else {
          // Третий клик - сбросить сортировку
          onSortChange(undefined);
        }
      } else {
        onSortChange({ field, order: 'asc' });
      }
    },
    [sort, onSortChange],
  );

  const getSortIcon = (field: string) => {
    if (sort?.field !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sort.order === 'asc' ? (
      <ArrowUp className="h-4 w-4 ml-1" />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1" />
    );
  };

  // Выбор строк
  const dataArray = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const allSelected =
    dataArray.length > 0 &&
    dataArray.every((row) => selectedIds.has(row[rowKey] as string | number));

  const someSelected =
    dataArray.some((row) => selectedIds.has(row[rowKey] as string | number)) &&
    !allSelected;

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(dataArray.map((row) => row[rowKey] as string | number)));
    }
  }, [allSelected, dataArray, rowKey]);

  const handleSelectRow = useCallback(
    (id: string | number) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    [],
  );

  const selectedRows = useMemo(
    () => dataArray.filter((row) => selectedIds.has(row[rowKey] as string | number)),
    [dataArray, selectedIds, rowKey],
  );

  // Уведомляем родителя об изменении выбранных строк
  useEffect(() => {
    onSelectionChange?.(selectedRows);
  }, [selectedRows, onSelectionChange]);

  // Очищаем выбор при смене страницы
  const handlePageChange = useCallback(
    (newPage: number) => {
      setSelectedIds(new Set());
      onPageChange(newPage);
    },
    [onPageChange],
  );

  // Фильтрация bulk actions
  const visibleBulkActions = useMemo(() => {
    if (!bulkActions) {
      return [];
    }
    return bulkActions.filter(
      (action) => !action.showOnlyWhenSelected || selectedIds.size > 0,
    );
  }, [bulkActions, selectedIds.size]);

  return (
    <div className="space-y-4">
      {/* Search, filters and bulk actions */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        {/* Search and filters */}
        {(onSearch || filters) && (
          <TableToolbar
            searchValue={currentSearchValue}
            onSearchChange={onSearch ? handleSearch : undefined}
            searchPlaceholder={searchPlaceholder}
            filters={filters}
            filtersCount={filtersCount}
            filterLabel={filterLabel}
            filterTitle={filterTitle}
            onResetFilters={onResetFilters}
          />
        )}

      </div>

      {/* Table & Pagination */}
      <div className="rounded-xl border border-border/60 bg-card shadow-sm overflow-hidden">
        {/* Панель выбора — вынесена из таблицы, чтобы не скроллилась горизонтально */}
        {selectedIds.size > 0 && visibleBulkActions.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border/60">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium">
                Выбрано:
                {' '}
                {selectedIds.size}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-muted-foreground hover:text-foreground"
                onClick={() => setSelectedIds(new Set())}
              >
                <X className="h-4 w-4 mr-1" />
                Сбросить
              </Button>
            </div>

            {/* Dropdown с действиями */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8">
                  Действия
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {visibleBulkActions.map((action, index) => (
                  <DropdownMenuItem
                    key={index}
                    onClick={() => action.onClick(selectedRows)}
                    className={cn(
                      action.variant === 'destructive' && 'text-destructive focus:text-destructive',
                    )}
                  >
                    {action.icon && <span className="mr-2">{action.icon}</span>}
                    {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        <div className="overflow-x-auto">
        <Table>
          <TableHeader className="overflow-hidden">
            <TableRow>
              {/* Checkbox column */}
              {showCheckboxes && (
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={allSelected}
                    data-indeterminate={someSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Выбрать все"
                  />
                </TableHead>
              )}

              {columns.map((column) => {
                const isSortable = column.sortable && onSortChange;

                return (
                  <TableHead
                    key={String(column.key)}
                    style={{ width: column.width }}
                    className={cn(
                      column.className,
                      isSortable && 'cursor-pointer select-none hover:bg-muted/50',
                      column.hiddenOnMobile && 'hidden md:table-cell',
                    )}
                    onClick={isSortable ? () => handleSort(String(column.key)) : undefined}
                  >
                    <div className="flex items-center">
                      {column.header}
                      {isSortable && getSortIcon(String(column.key))}
                    </div>
                  </TableHead>
                );
              })}

              {/* Legacy actions column */}
              {actions && !useBulkActions && (
                <TableHead className="w-[100px]">Действия</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    (showCheckboxes ? 1 : 0) +
                    (actions && !useBulkActions ? 1 : 0)
                  }
                  className="h-32 text-center"
                >
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Загрузка...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (dataArray.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    (showCheckboxes ? 1 : 0) +
                    (actions && !useBulkActions ? 1 : 0)
                  }
                  className="h-32 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              dataArray.map((record) => {
                const recordId = record[rowKey] as string | number;
                const isSelected = selectedIds.has(recordId);

                return (
                  <TableRow
                    key={recordId}
                    className={cn(
                      onRowClick && 'cursor-pointer',
                      isSelected && 'bg-muted/50',
                      'hover:bg-muted/50',
                    )}
                    onClick={() => onRowClick?.(record)}
                  >
                    {/* Checkbox cell */}
                    {showCheckboxes && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleSelectRow(recordId)}
                          aria-label={`Выбрать запись ${recordId}`}
                        />
                      </TableCell>
                    )}

                    {columns.map((column) => {
                      const value = getValue(record, String(column.key));
                      return (
                        <TableCell
                          key={String(column.key)}
                          className={cn(
                            column.className,
                            column.hiddenOnMobile && 'hidden md:table-cell',
                          )}
                        >
                          {column.render
                            ? column.render(value, record)
                            : String(value ?? '-')}
                        </TableCell>
                      );
                    })}

                    {/* Legacy actions cell */}
                    {actions && !useBulkActions && (
                      <TableCell onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        {actions(record)}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            ))}
          </TableBody>
        </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-3 py-3 md:px-4 md:py-4 border-t gap-2">
          <div className="text-xs md:text-sm text-muted-foreground shrink-0">
            {total > 0 ? (
              <>
                <span className="hidden sm:inline">
                  Показано
                  {' '}
                  {(page - 1) * pageSize + 1}
                  {' '}
                  —
                  {' '}
                  {Math.min(page * pageSize, total)}
                  {' '}
                  из
                  {' '}
                </span>
                <span className="sm:hidden">
                  {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)}
                  {' / '}
                </span>
                {total}
              </>
            ) : (
              'Нет записей'
            )}
          </div>
          <div className="flex items-center gap-1 md:gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 md:h-9 md:w-9"
              onClick={() => handlePageChange(1)}
              disabled={!canPreviousPage || isLoading}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 md:h-9 md:w-9"
              onClick={() => handlePageChange(page - 1)}
              disabled={!canPreviousPage || isLoading}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs md:text-sm px-1 md:px-2 tabular-nums">
              {page}
              {' / '}
              {totalPages || 1}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 md:h-9 md:w-9"
              onClick={() => handlePageChange(page + 1)}
              disabled={!canNextPage || isLoading}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 md:h-9 md:w-9"
              onClick={() => handlePageChange(totalPages)}
              disabled={!canNextPage || isLoading}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
